import type { UserStructuredProps } from "../lib/api";
import { normalizeCustomName, QUICK_ADD_SPECS, RESERVED_PROPERTY_IDS } from "../lib/quickAddProps";

interface StructuredPropertiesEditorProps {
  value: UserStructuredProps;
  onChange: (next: UserStructuredProps) => void;
}

// StructuredPropertiesEditor is the single shared "Static Metadata" section
// composed into every connector's Add/Edit Source dialog — fixed
// Firmware/Software Version fields, a Quick Add list mirroring the devprops
// table, and free-form custom attributes. All three funnel into the same
// UserStructuredProps shape the backend validates and applies to every
// Dataset the source emits.
export function StructuredPropertiesEditor({ value, onChange }: StructuredPropertiesEditorProps) {
  const quickAdd = value.quickAdd ?? {};
  const custom = value.custom ?? [];
  const activeQuickAddIds = new Set(Object.keys(quickAdd));

  const setFixed = (key: "firmwareVersion" | "softwareVersion", next: string) => {
    onChange({ ...value, [key]: next });
  };

  const addQuickAdd = (propertyId: string) => {
    onChange({ ...value, quickAdd: { ...quickAdd, [propertyId]: "" } });
  };
  const setQuickAdd = (propertyId: string, next: string) => {
    onChange({ ...value, quickAdd: { ...quickAdd, [propertyId]: next } });
  };
  const removeQuickAdd = (propertyId: string) => {
    const next = { ...quickAdd };
    delete next[propertyId];
    onChange({ ...value, quickAdd: next });
  };

  const addCustomRow = () => {
    onChange({ ...value, custom: [...custom, { name: "", value: "" }] });
  };
  const setCustomRow = (index: number, patch: Partial<{ name: string; value: string }>) => {
    const next = custom.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange({ ...value, custom: next });
  };
  const removeCustomRow = (index: number) => {
    onChange({ ...value, custom: custom.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-4 rounded border border-rule bg-surface p-4">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
          Static Metadata
        </span>
        <span className="text-[10.5px] italic text-ash">optional</span>
      </div>

      <div className="flex flex-wrap gap-2">
        <span className="self-center text-[11.5px] text-ash">Quick add:</span>
        {QUICK_ADD_SPECS.filter((s) => !activeQuickAddIds.has(s.propertyId)).map((s) => (
          <button
            key={s.propertyId}
            type="button"
            onClick={() => addQuickAdd(s.propertyId)}
            className="rounded-full border border-rule bg-surface px-2.5 py-1 text-[11px] text-ink hover:border-ink/30"
          >
            + {s.label}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        <FixedRow
          label="Firmware Version"
          value={value.firmwareVersion ?? ""}
          placeholder="v2.9.0"
          onChange={(v) => setFixed("firmwareVersion", v)}
        />
        <FixedRow
          label="Software Version"
          value={value.softwareVersion ?? ""}
          placeholder="v17.0"
          onChange={(v) => setFixed("softwareVersion", v)}
        />
        {QUICK_ADD_SPECS.filter((s) => activeQuickAddIds.has(s.propertyId)).map((s) => {
          const raw = quickAdd[s.propertyId] ?? "";
          const error =
            raw !== "" && s.pattern && !s.pattern.test(raw) ? s.patternError : undefined;
          return (
            <FixedRow
              key={s.propertyId}
              label={s.label}
              value={raw}
              placeholder={s.format}
              error={error}
              onChange={(v) => setQuickAdd(s.propertyId, v)}
              onRemove={() => removeQuickAdd(s.propertyId)}
            />
          );
        })}
      </div>

      <div className="space-y-2">
        {custom.map((row, i) => (
          <CustomRow
            key={i}
            name={row.name}
            value={row.value}
            existingNames={custom.filter((_, j) => j !== i).map((r) => r.name)}
            onChangeName={(n) => setCustomRow(i, { name: n })}
            onChangeValue={(v) => setCustomRow(i, { value: v })}
            onRemove={() => removeCustomRow(i)}
          />
        ))}
        <button
          type="button"
          onClick={addCustomRow}
          className="rounded border border-rule bg-surface px-2.5 py-1.5 text-[11.5px] text-ink hover:border-ink/30"
        >
          + Add attribute
        </button>
      </div>
    </div>
  );
}

interface FixedRowProps {
  label: string;
  value: string;
  placeholder?: string;
  error?: string;
  onChange: (next: string) => void;
  onRemove?: () => void;
}

// FixedRow renders a read-only label (fixed field or an active quick-add
// entry) paired with an editable value input.
function FixedRow({ label, value, placeholder, error, onChange, onRemove }: FixedRowProps) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="w-40 shrink-0 truncate text-[12.5px] text-ink">{label}</span>
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="input flex-1"
        />
        {onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className="shrink-0 rounded border border-rule bg-surface px-2 py-1 text-[11px] text-rose-700 hover:border-rose-500/50"
          >
            Remove
          </button>
        ) : null}
      </div>
      {error ? <div className="mt-1 pl-40 text-[11px] text-rose-700">{error}</div> : null}
    </div>
  );
}

interface CustomRowProps {
  name: string;
  value: string;
  existingNames: string[];
  onChangeName: (next: string) => void;
  onChangeValue: (next: string) => void;
  onRemove: () => void;
}

function CustomRow({
  name,
  value,
  existingNames,
  onChangeName,
  onChangeValue,
  onRemove,
}: CustomRowProps) {
  const normalized = name.trim() ? normalizeCustomName(name) : "";
  const collidesWithReserved = normalized !== "" && RESERVED_PROPERTY_IDS.includes(normalized);
  const collidesWithCustom =
    normalized !== "" &&
    existingNames.some((n) => n.trim() && normalizeCustomName(n) === normalized);
  const error = collidesWithReserved
    ? `"${normalized}" collides with a reserved property name`
    : collidesWithCustom
      ? `"${normalized}" is already used by another custom attribute`
      : undefined;

  return (
    <div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={name}
          placeholder="Attribute name"
          onChange={(e) => onChangeName(e.target.value)}
          className="input w-40 shrink-0"
        />
        <input
          type="text"
          value={value}
          placeholder="Value"
          onChange={(e) => onChangeValue(e.target.value)}
          className="input flex-1"
        />
        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 rounded border border-rule bg-surface px-2 py-1 text-[11px] text-rose-700 hover:border-rose-500/50"
        >
          Remove
        </button>
      </div>
      {error ? <div className="mt-1 pl-40 text-[11px] text-rose-700">{error}</div> : null}
    </div>
  );
}

// isStructuredPropertiesValid reports whether the current value has any
// client-detectable validation error (quick-add format, custom name
// collision/duplicate). Callers can use this to block submit — the server
// re-validates regardless.
export function isStructuredPropertiesValid(value: UserStructuredProps): boolean {
  const quickAdd = value.quickAdd ?? {};
  for (const spec of QUICK_ADD_SPECS) {
    const raw = quickAdd[spec.propertyId];
    if (raw && spec.pattern && !spec.pattern.test(raw)) return false;
  }
  const custom = value.custom ?? [];
  const seen = new Set<string>();
  for (const row of custom) {
    if (!row.name.trim() || !row.value.trim()) continue;
    const id = normalizeCustomName(row.name);
    if (!id || RESERVED_PROPERTY_IDS.includes(id) || seen.has(id)) return false;
    seen.add(id);
  }
  return true;
}

// Also exported so callers can seed a blank editor without importing
// UserStructuredProps directly.
export const EMPTY_STRUCTURED_PROPS: UserStructuredProps = {};
